import { useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { useSnapshot } from 'valtio';

import { useTheme } from '@mui/material/styles';

import { Typography } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import Fab from '@mui/material/Fab';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';

import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';

import { useBreakpoints } from '@/hooks';

import { AppUser, appStore, getRoleLabel } from '@/store';

function UserMenu() {
  const theme = useTheme();
  const auth = useAuth0();
  const { isMobileDevice } = useBreakpoints();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const appSnap = useSnapshot(appStore);
  const userProfile = appSnap.user as AppUser;

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleLogoutClick = () => {
    // log out
    auth.logout({ logoutParams: { returnTo: window.location.origin } });
    handleCloseMenu();
  };

  return (
    <>
      <Fab
        variant={isMobileDevice ? 'circular' : 'extended'}
        size="small"
        onClick={handleOpenMenu}
        sx={{
          bgcolor: 'background.paper',
          boxShadow: theme.shadows[1],
          display: 'flex',
          gap: 1,
          pl: `${!userProfile || !userProfile.avatar ? 4 : 8}px !important`,
        }}
      >
        {userProfile ? (
          <>
            {userProfile.avatar ? (
              <Avatar
                sx={{ width: 28, height: 28 }}
                src={userProfile.avatar}
                slotProps={{
                  img: { sx: { objectFit: 'contain' } },
                }}
              />
            ) : (
              <Avatar sx={{ width: 28, height: 28 }}>
                <Typography>{userProfile.company.charAt(0)}</Typography>
              </Avatar>
            )}
            {!isMobileDevice && (
              <Typography variant="caption" textTransform={'none'}>
                {userProfile.displayName}
              </Typography>
            )}
          </>
        ) : (
          <Avatar sx={{ width: 28, height: 28 }}>
            <PersonIcon />
          </Avatar>
        )}
      </Fab>
      <Menu
        id="appbar-usermenu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
      >
        {userProfile && [
          <MenuItem
            key={'user-menu-header'}
            disableRipple
            sx={{ cursor: 'default' }}
          >
            <CardHeader
              avatar={
                <Tooltip title={userProfile.company}>
                  {userProfile.avatar ? (
                    <Avatar
                      src={userProfile.avatar}
                      slotProps={{ img: { sx: { objectFit: 'contain' } } }}
                    />
                  ) : (
                    <Avatar>{userProfile.company.charAt(0)}</Avatar>
                  )}
                </Tooltip>
              }
              title={userProfile.email}
              subheader={getRoleLabel(userProfile.role)}
            />
          </MenuItem>,
          <Divider key="user-menu-header-divider" />,
        ]}
        {/* <MenuItem sx={{ p: 2, pl: 4, pr: 4 }} onClick={handleSettingsClick}>
          <ListItemIcon>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem> */}
        <MenuItem
          sx={{ p: 2, pl: 4, pr: 4 }}
          key="user-menu-item-logout"
          onClick={handleLogoutClick}
        >
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}

export { UserMenu };
